import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../config';

export interface CreateReleaseInput {
  version: string;
  channel: 'stable' | 'beta' | 'alpha';
  platform: 'win32' | 'darwin';
  architecture: 'x64' | 'arm64' | 'ia32';
  fileUrl: string;
  fileSize: number;
  sha512: string;
  releaseNotes?: string;
  minimumAppVersion?: string;
  minimumOsVersion?: string;
  isMandatory: boolean;
  isDraft?: boolean;
  rolloutPercentage?: number;
}

export function useCreateRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateReleaseInput) => {
      return await api.post('/api/v1/updates/admin/upload', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['releaseAnalytics'] });
    },
  });
}

export function useToggleRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ releaseId, isActive }: { releaseId: number; isActive: boolean }) => {
      return await api.patch(`/api/v1/updates/admin/releases/${releaseId}`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
  });
}

export interface UpdateReleaseInput {
  channel?: 'stable' | 'beta' | 'alpha';
  releaseNotes?: string;
  minimumAppVersion?: string;
  minimumOsVersion?: string;
  isMandatory?: boolean;
  isDraft?: boolean;
  rolloutPercentage?: number;
}

export function useUpdateRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ releaseId, data }: { releaseId: number; data: UpdateReleaseInput }) => {
      return await api.patch(`/api/v1/updates/admin/releases/${releaseId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['releaseAnalytics'] });
    },
  });
}

export function usePublishRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (releaseId: number) => {
      return await api.post(`/api/v1/updates/admin/releases/${releaseId}/publish`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
  });
}

export function useDeleteRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ releaseId, reason }: { releaseId: number; reason?: string }) => {
      return await api.delete(`/api/v1/updates/admin/releases/${releaseId}`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['releaseAnalytics'] });
      queryClient.invalidateQueries({ queryKey: ['releases', 'trash'] });
    },
  });
}

export function useRestoreRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ releaseId, reason }: { releaseId: number; reason?: string }) => {
      return await api.post(`/api/v1/updates/admin/releases/${releaseId}/restore`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases'] });
      queryClient.invalidateQueries({ queryKey: ['releases', 'trash'] });
    },
  });
}

export function usePermanentlyDeleteRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ releaseId, reason }: { releaseId: number; reason?: string }) => {
      return await api.delete(`/api/v1/updates/admin/releases/${releaseId}/permanent`, {
        confirmation: 'PERMANENTLY DELETE',
        reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releases', 'trash'] });
    },
  });
}